
import Vue, { PropType } from "vue";
import { format } from "date-fns-tz";
import { isValid, parse } from "date-fns";

type NullableString = string | null | undefined;

interface PhoneInfoItem {
  firstName: string;
  locationName: string;
  postedStart?: NullableString;
  postedEnd?: NullableString;
  ShiftDate?: NullableString;
  firstBooked?: NullableString;
  phone?: NullableString;
}

const FALLBACK_USER_NAME = "TempStars Admin";
const BACKEND_SHIFT_FORMAT = "MMM d, yyyy";
const DISPLAY_SHIFT_FORMAT = "eeee, MMM d, yyyy";

/**
 * Returns a normalized admin name for modal messaging.
 */
const resolveStoredUserName = (raw: NullableString): string => {
  if (!raw) {
    return FALLBACK_USER_NAME;
  }

  const trimmed = raw.trim().replace(/^"|"$/g, "");

  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed === "string") {
      return parsed || FALLBACK_USER_NAME;
    }

    if (parsed && typeof parsed === "object") {
      const parsedObj = parsed as Partial<{
        firstName: string;
        lastName: string;
        email: string;
      }>;
      const { firstName, lastName, email } = parsedObj;
      const fullName = [firstName, lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      return fullName || email || FALLBACK_USER_NAME;
    }
  } catch (error) {
    // Ignore JSON parse errors and fall back to the stored string value.
  }

  return trimmed || FALLBACK_USER_NAME;
};

/**
 * Parses the backend-provided shift date into a Date or null.
 */
const parseShiftDate = (value: NullableString): Date | null => {
  if (!value || typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const directDate = new Date(trimmed);
  if (isValid(directDate)) {
    return directDate;
  }

  const normalized = trimmed.replace(/\s+/g, " ");
  const parsedDate = parse(normalized, BACKEND_SHIFT_FORMAT, new Date());
  return isValid(parsedDate) ? parsedDate : null;
};

export default Vue.extend({
  name: "PhoneInfoModal",
  props: {
    item: {
      type: Object as PropType<PhoneInfoItem>,
      required: true
    }
  },
  data: () => {
    return {
      dialog: false,
      user: resolveStoredUserName(localStorage.getItem("user"))
    };
  },
  computed: {
    formattedShiftDate(): string {
      const parsedDate = parseShiftDate(this.item.ShiftDate);
      return parsedDate
        ? format(parsedDate, DISPLAY_SHIFT_FORMAT)
        : "Shift date pending";
    },
    checkFB(): boolean {
      return this.item.firstBooked === "First Shift";
    }
  }
});
